import React from 'react'
import { Avatar, Box, Stack, Typography } from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import AiSparkIcon from '../../../assets/icons/AiSparkIcon'
import MarkdownMessage from './MarkdownMessage'
import FilterCard from './FilterCard'
import ChatRetryAction from './ChatRetryAction'
import ProgressStatusLabel, {
    AI_CHAT_PROGRESS_PLACEHOLDER,
    aiAssistantProgressRowPulseSx,
} from './progressStatusLabel'

import type { ChatTurnMessage } from './types'

type Props = {
    message: ChatTurnMessage
    /** True when this assistant message immediately follows the user's in the same turn. */
    followsUserMessage?: boolean
    runInFlight?: boolean
    userAvatarUrl?: string
    userName?: string
    onUndoFilters?: (messageId: string) => void
    undoDisabled?: boolean
    undoDisabledReason?: string
    /** Optional content rendered below the assistant reply (e.g. chart apply results). */
    footer?: React.ReactNode
    onRetry?: (prompt: string) => void
    retryDisabled?: boolean
}

function userInitial(name?: string): string | null {
    if (!name) return null
    const trimmed = name.trim()
    return trimmed ? trimmed[0].toUpperCase() : null
}

function StreamingCaret() {
    return (
        <Box
            component="span"
            aria-hidden
            sx={{
                display: 'inline-block',
                width: '0.55em',
                height: '1em',
                ml: 0.25,
                verticalAlign: 'text-bottom',
                bgcolor: 'text.primary',
                animation: 'aiChatCaretBlink 1s step-end infinite',
                '@keyframes aiChatCaretBlink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                },
            }}
        />
    )
}

export default function ChatTurn({
    message,
    followsUserMessage = false,
    runInFlight = false,
    userAvatarUrl,
    userName,
    onUndoFilters,
    undoDisabled,
    undoDisabledReason,
    footer,
    onRetry,
    retryDisabled,
}: Props) {
    const {
        id,
        role,
        content,
        streaming,
        filters,
        filterStatus,
        showRetry,
        retryPrompt,
        progressLabel,
    } = message

    const awaitingFirstText = role === 'assistant' && !!streaming && !content.trim()
    const progressText =
        progressLabel || (awaitingFirstText && runInFlight ? AI_CHAT_PROGRESS_PLACEHOLDER : null)
    const showProgressState = awaitingFirstText && !!progressText

    if (role === 'user') {
        const initial = userInitial(userName)
        return (
            <Stack direction="row" justifyContent="flex-end" alignItems="flex-start" spacing={1}>
                <Box
                    sx={{
                        maxWidth: 'calc(100% - 36px)',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: 'action.hover',
                        border: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Typography variant="body2" whiteSpace="pre-wrap" sx={{ wordBreak: 'break-word' }}>
                        {content}
                    </Typography>
                </Box>
                <Avatar
                    src={userAvatarUrl || undefined}
                    alt={userName || 'You'}
                    sx={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        bgcolor: 'action.selected',
                        color: 'text.secondary',
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    {initial ?? <PersonOutlineIcon sx={{ fontSize: 16 }} />}
                </Avatar>
            </Stack>
        )
    }

    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1}
            sx={{
                mt: followsUserMessage ? 2.5 : 0,
                ...(showProgressState ? aiAssistantProgressRowPulseSx : {}),
            }}
        >
            <Avatar
                sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    mt: 0.25,
                }}
            >
                <AiSparkIcon style={{ fontSize: 26 }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
                {showProgressState ? (
                    <ProgressStatusLabel label={progressText} />
                ) : (
                    <>
                        <Typography variant="caption" color="text.tertiary" display="block" sx={{ mb: 0.75 }}>
                            Assistant
                        </Typography>
                        <MarkdownMessage content={content} streaming={streaming} />
                    </>
                )}
                {streaming && !showProgressState && <StreamingCaret />}
                {filters && filterStatus && (
                    <FilterCard
                        count={filters.length}
                        status={filterStatus}
                        runInFlight={runInFlight}
                        undoDisabled={undoDisabled}
                        undoDisabledReason={undoDisabledReason}
                        onUndo={() => onUndoFilters?.(id)}
                    />
                )}
                {footer}
                {showRetry && retryPrompt && onRetry && (
                    <ChatRetryAction
                        onRetry={() => onRetry(retryPrompt)}
                        disabled={retryDisabled}
                    />
                )}
            </Box>
        </Stack>
    )
}
